import React, { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";

/**
 * An image that shows a spinner until it has actually loaded.
 *
 * The pictures on a policy — Aadhaar scans, error screenshots, payment QRs —
 * come from S3 presigned URLs and are full-resolution captures. On anything but
 * a fast line they take seconds, and until then the browser draws nothing at
 * all: a blank rectangle that looks exactly like a missing document. People
 * reload the page, or report the screenshot as lost.
 *
 * The spinner sits in a box of the FINAL size, so the page does not jump when
 * the picture lands.
 *
 * A URL that fails — an expired presigned link is the usual reason — says so
 * rather than leaving the broken-image glyph, because "this link has expired"
 * and "this document was never uploaded" call for different things.
 */
const LoadingImage = ({
  src,
  alt = "",
  height = 200,
  width = "100%",
  objectFit = "contain",
  sx = {},
  imgSx = {},
  onClick,
  ...rest
}) => {
  const [state, setState] = useState("loading"); // loading | loaded | failed

  return (
    <Box
      sx={{
        position: "relative",
        width,
        height,
        backgroundColor: "#f9fafb",
        borderRadius: "6px",
        overflow: "hidden",
        cursor: onClick && state === "loaded" ? "pointer" : "default",
        ...sx,
      }}
      onClick={state === "loaded" ? onClick : undefined}
    >
      {state === "loading" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={26} thickness={4} sx={{ color: "#1E3A5F" }} />
        </Box>
      )}

      {state === "failed" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            px: 1,
            textAlign: "center",
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 26, color: "#98a2b3" }} />
          <Typography sx={{ fontSize: 11.5, color: "#667085" }}>
            Could not load this image — its link may have expired.
          </Typography>
        </Box>
      )}

      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setState("loaded")}
        onError={() => setState("failed")}
        sx={{
          width: "100%",
          height: "100%",
          objectFit,
          display: "block",
          // Kept mounted while loading so onLoad still fires; just not shown.
          opacity: state === "loaded" ? 1 : 0,
          transition: "opacity .2s",
          ...imgSx,
        }}
        {...rest}
      />
    </Box>
  );
};

export default LoadingImage;
